import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { SYSTEMS } from '../../constants/systems';
import { useSwitchState } from '../../hooks/useSwitchState';
import { useTemperature } from '../../hooks/useTemperature';
import { SWITCH_SLICES } from '../store/slices/switchSlices';
import { selectUserPermissions } from '../store/slices/userSlice';
import { flexBoxCenter, flexDirectionColumn, layerADark } from '../styles/commonStyles';
import MCConstantTemp from '../commonComponentsMC/controllers/MCConstantTemp';
import MCDisplayBoxWrapper from '../commonComponentsMC/controllers/MCDisplayBoxWrapper';
import MCFanOnly from '../commonComponentsMC/controllers/MCFanOnly';
import MCHeatingSchedule from '../commonComponentsMC/controllers/MCHeatingSchedule';
import MCInstantHeat from '../commonComponentsMC/controllers/MCInstantHeat';
import MCSnowSensor from '../commonComponentsMC/controllers/MCSnowSensor';
import MCWindFactor from '../commonComponentsMC/controllers/MCWindFactor';
import MachineTelemetry from '../commonComponentsMC/MachineTelemetry';
import HeaterStatus from '../commonComponentsMC/HeaterStatus';
import InputTempMessage from '../userMessages/inputTempMessage';
import ConflictMessage from '../masterControl/userMessages/ConflictMessage';
import MobileProgramPicker from './MobileProgramPicker';
import { inputMessage } from './inputMessages';
import { useProgramCommands } from './useProgramCommands';

// The text a widget's temperature field starts from: the stored °C in the
// operator's unit, or empty when there is none. The widget edits it while
// the operator types; a change to the program or the unit resets it.
const useShownTemp = (program, field) => {
  const { isF, unit, toDisplay } = useTemperature();
  const [text, setText] = useState('');
  useEffect(() => {
    const celsius = program?.[field];
    setText(celsius > 0 ? `${toDisplay(celsius)} ${unit}` : '');
  }, [program, isF]);
  return [text, setText];
};

/**
 * One device's program controls for ESS, TGS or TES (`swtName`): the widget
 * column on desktop, or the program picker and the picked program's panel on
 * mobile. `SYSTEMS[swtName].programs` decides which widgets show, in order.
 */
const ControlBox = ({ swtName, location, machine, isMobile, setTemp, indivLocationName }) => {
  const { programs, hasSSRs } = SYSTEMS[swtName];
  const actions = SWITCH_SLICES[swtName];
  const { flat } = useSwitchState(swtName);
  const device = flat[location][machine];
  const {
    deviceMac,
    deviceStatus,
    isFaults,
    isOff,
    instantHeat,
    snowSensor,
    optionalConstantTemp,
    heatingSchedule,
    heatingScheduleList,
    windFactor,
    fanOnly,
    isWifi,
    isFanDisabled,
    mobileSelectedProgram,
    displayConflictMessage,
    devicesConflicts,
  } = device;

  const dispatch = useDispatch();
  const { isF, unit, toDisplay } = useTemperature();
  const permissions = useSelector(selectUserPermissions);
  const isDisabled = device.isDisabled || !permissions.WRITE;

  const [instantHeatTemp, setInstantHeatTemp] = useShownTemp(instantHeat, 'inputTemp');
  const [constantTemp, setConstantTemp] = useShownTemp(optionalConstantTemp, 'inputTemp');
  const [snowSensorTemp] = useShownTemp(snowSensor, 'defaultTemp');
  const [schedulerTemp, setSchedulerTemp] = useState('');
  const [message, setMessage] = useState(null);

  // The first schedule row fills the schedule field and marks the program
  // ready.
  useEffect(() => {
    const first = heatingScheduleList[0].inputTemp;
    setSchedulerTemp(first > 0 ? `${toDisplay(first)} ${unit}` : '');
    dispatch(actions.setScheduleReady({ location, machine, state: first > 0 }));
  }, [heatingScheduleList, isF]);

  const { handleOnClick, confirmConflict, cancelConflict } = useProgramCommands({
    system: swtName,
    location,
    machine,
    device,
    showMessage: (id, program) => {
      const lines = inputMessage(id, program);
      if (lines) setMessage({ title: program, lines });
    },
    clearInstantHeatInput: () => setInstantHeatTemp(''),
  });

  // A program counts as running only while the device reports its mode.
  const runs = (program, mode) => program.isActivated && device.op_mode === mode;

  // Mobile shows one program's panel at a time.
  const pickProgram = (program) => {
    dispatch(actions.unselectPrograms({ location, machine }));
    if (program !== 'unselect' && !mobileSelectedProgram[program]) {
      dispatch(actions.selectProgram({ location, machine, program }));
    }
  };

  const widget = (program) => {
    const props = { handleOnClick, isDisabled, isMobile, swtName, location, machine };
    switch (program) {
      case 'instantHeat':
        return (
          <MCInstantHeat
            key={program}
            {...props}
            isActivated={runs(instantHeat, 'SWITCH')}
            isReady={instantHeat.isReady}
            tempInput={instantHeatTemp}
            setTempInput={setInstantHeatTemp}
          />
        );
      case 'fanOnly':
        return (
          <MCFanOnly
            key={program}
            {...props}
            isActivated={fanOnly}
            isWifi={isWifi}
            isFanDisabled={isFanDisabled}
          />
        );
      case 'snowSensor':
        return (
          <MCSnowSensor
            key={program}
            {...props}
            isActivated={runs(snowSensor, 'SNOW')}
            isReady={snowSensor.isReady}
            defaultTemp={snowSensorTemp}
          />
        );
      case 'optionalConstantTemp':
        return (
          <MCConstantTemp
            key={program}
            {...props}
            isActivated={optionalConstantTemp.isActivated}
            isReady={optionalConstantTemp.isReady}
            tempInput={constantTemp}
            setTempInput={setConstantTemp}
          />
        );
      case 'heatingSchedule':
        return (
          <MCHeatingSchedule
            key={program}
            {...props}
            heatingScheduleList={heatingScheduleList}
            isActivated={runs(heatingSchedule, 'SCHEDULE')}
            isReady={heatingSchedule.isReady}
            tempInput={schedulerTemp}
            setTempInput={setSchedulerTemp}
          />
        );
      case 'windFactor':
        return (
          <MCWindFactor
            key={program}
            {...props}
            isActivated={runs(windFactor, 'WIND')}
            isReady={windFactor.isReady}
          />
        );
      default:
        return null;
    }
  };

  const conflict = displayConflictMessage && (
    <ConflictMessage
      currentSwitch={devicesConflicts.currentSwitch}
      DesiredSwitch={devicesConflicts.desiredSwitch}
      handleCancel={cancelConflict}
      handleConfirm={confirmConflict}
    />
  );

  if (isMobile) {
    return (
      <Wrapper>
        <MobileProgramPicker programs={programs} device={device} onPick={pickProgram} />
        {programs.filter((program) => mobileSelectedProgram[program]).map(widget)}
        <MachineTelemetry
          swtName={swtName}
          location={location}
          machine={machine}
          setTemp={setTemp}
        />
        {conflict}
        {hasSSRs && (
          <HeaterStatus
            isMobile={true}
            swtName={swtName}
            location={location}
            machine={machine}
            isFaults={isFaults}
            isOff={isOff}
            indivLocationName={indivLocationName}
          />
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <SectionHeader>
        <HeaderHatSvg
          src={isFaults ? '/images/controller-hat-faults.svg' : '/images/controller-hat.svg'}
        />
        <HeaderTitleWrapper>
          <Title right={true}>{swtName} - controls</Title>
          <Title right={false}>id : {deviceStatus === 'NOT_LINKED' ? '' : deviceMac}</Title>
        </HeaderTitleWrapper>
      </SectionHeader>

      <SectionMain isFaults={isFaults}>
        <MainInnerWrapper>
          {programs.map(widget)}
          <MCDisplayBoxWrapper swtName={swtName} location={location} machine={machine} />
        </MainInnerWrapper>
      </SectionMain>

      {message && (
        <MessageBoxWrapper>
          <InputTempMessage
            onClose={() => setMessage(null)}
            messages={message.lines}
            title={message.title}
          />
        </MessageBoxWrapper>
      )}

      {conflict}
    </Wrapper>
  );
};

export default ControlBox;

const Wrapper = styled.div`
  width: 413px;
  ${flexBoxCenter}
  display: flex;
  flex-direction: column;
  position: relative;
`;

const SectionHeader = styled.section`
  width: 100%;
  position: relative;
`;

const HeaderTitleWrapper = styled.div`
  position: absolute;
  top: 3px;
  padding: 0 6px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.span`
  font-size: 14px;
  letter-spacing: 1.4px;

  ${(p) =>
    p.right
      ? css`
          margin-left: 2px;
        `
      : css`
          width: 53%;
          font-size: 12px;
          letter-spacing: 1.2px;
        `}
`;

const HeaderHatSvg = styled.img`
  position: relative;
`;

const SectionMain = styled.section`
  width: 413px;
  height: 665px;

  background: transparent linear-gradient(180deg, #20354e 0%, #060d19 100%);
  border: 1px solid #000000;
  border-radius: 0px 12px 12px 12px;

  ${flexBoxCenter}

  ${({ isFaults }) =>
    isFaults &&
    css`
      border: 1px solid red;
    `}
`;

const MainInnerWrapper = styled.div`
  width: 401px;
  height: 654px;
  border-radius: 8px;
  ${layerADark};
  ${flexDirectionColumn};
  padding: 1.5px 0;
`;

const MessageBoxWrapper = styled.div`
  width: 1180px;
  height: 600px;
  position: absolute;

  ${flexBoxCenter}

  top: 0;
  left: 0;
  z-index: 100;
`;
